import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { isStringArray } from 'utils/isArray';
import isObject from './isObject';

export type Action = {
  type: string;
  value: unknown;
};
export const isAction = (action: unknown): action is Action => {
  if (!isObject(action)) return false;
  return (
    typeof (action as Action).type === 'string' &&
    typeof (action as Action) !== 'undefined'
  );
};

export type TypedAction<T> = {
  type: string;
  value: T;
};

export const isNumberAction = (
  action: unknown,
): action is TypedAction<number> => {
  return isAction(action) && typeof action.value === 'number';
};

export const isStringAction = (
  action: unknown,
): action is TypedAction<string> => {
  return isAction(action) && typeof action.value === 'string';
};

/**
 * @example
 *
 * const action1 = {
 * 	type: 'type1',
 *  	value: {
 * 		color: ['blue'],
 * 		size: ['16'],
 * 	}
 * }
 * const action2 = {
 * 	type: 'type2',
 *		value: {
 * 		color: ['blue'],
 *			height: ['16'],
 * 	}
 * }
 * }
 * const action3 = {
 * 	type: 'type3',
 *		value: {
 * 		color: ['blue'],
 *			size: [16],
 * 	}
 * }
 *
 * isTagsAction<'color' | 'size'>(['color', 'size'], action1) // returns true
 * isTagsAction<'color' | 'size'>(['color', 'size'], action2) // returns false because height is not specified
 * isTagsAction<'color' | 'size'>(['color', 'size'], action3) // returns false because 16 is not a string
 */
export const isTagsAction = <T extends string>(
  tagNames: readonly T[],
  action: unknown,
): action is TypedAction<Tags<T>> => {
  if (!(isAction(action) && isObject(action.value))) return false;
  const keys = Object.keys(action.value);
  const values = Object.values(action.value);
  for (let i = 0; i < values.length; i++) {
    if (!tagNames.includes(keys[i] as T)) return false;
    if (!isStringArray(values[i])) return false;
  }
  return true;
};

/**
 * @example
 *
 * const action1 = {
 * 	type: 'type1',
 *  	value: {
 * 		color: true,
 * 		size: true,
 * 	}
 * }
 * const action2 = {
 * 	type: 'type2',
 *		value: {
 * 		color: true,
 *			height: true,
 * 	}
 * }
 * }
 * const action3 = {
 * 	type: 'type3',
 *		value: {
 * 		color: true,
 *			size: 2,
 * 	}
 * }
 *
 * isTagsAction<'color' | 'size'>(['color', 'size'], action1) // returns true
 * isTagsAction<'color' | 'size'>(['color', 'size'], action2) // returns false because height is not specified
 * isTagsAction<'color' | 'size'>(['color', 'size'], action3) // returns false because 2 is not a boolean
 */
export const isColumns = <ColumnNames extends string>(
  columnNames: readonly ColumnNames[],
  action: unknown,
): action is TypedAction<{ [key in ColumnNames]: boolean }> => {
  if (!(isAction(action) && isObject(action.value))) return false;
  const keys = Object.keys(action.value);
  const values = Object.values(action.value);
  for (let i = 0; i < values.length; i++) {
    if (!columnNames.includes(keys[i] as ColumnNames)) return false;
    if (typeof values[i] !== 'boolean') return false;
  }
  return true;
};

export const isOrderAction = <SortingValue extends string>(
  sortableColumns: readonly SortingValue[],
  action: unknown,
): action is TypedAction<GetOrderValue<SortingValue>> => {
  if (!isStringAction(action)) return false;
  const split = action.value.split('/');
  if (split.length !== 2) return false;
  if (split[0] !== 'asc' && split[0] !== 'desc') return false;

  if (!sortableColumns.includes(split[1] as SortingValue)) return false;

  return true;
};

export const isObjectAction = <ObjectType extends Record<string, unknown>>(
  keys: readonly (keyof ObjectType)[],
  action: unknown,
): action is TypedAction<Record<keyof ObjectType, unknown>> => {
  if (!(isAction(action) && isObject(action.value))) return false;
  const valueKeys = Object.keys(action.value);
  for (let i = 0; i < keys.length; i++) {
    if (!valueKeys.includes(keys[i] as string)) return false;
  }

  return true;
};
